import type { ApiClient, MillsheetAutoLink } from '@mbfab/client-api'
import { api } from '@mbfab/client-api'
import { isSome, SupportingMimeTypes } from '@mbfab/shared'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { omit } from 'es-toolkit'
import { useState } from 'react'

import { AppSidebarContainer } from '@/components/app/app-sidebar-container'
import { autoLinkWarning } from '@/components/screens/upload/auto-link-warning'
import type { PdfUploadFileStatus } from '@/components/screens/upload/pdf-dropzone'
import type { Feedback } from '@/components/screens/upload/types'
import type { UploadFileType } from '@/components/screens/upload/upload-file-type'
import { isMillsheetLinkedUploadType } from '@/components/screens/upload/upload-file-type'
import type { UploadOrigin } from '@/components/screens/upload/upload-origin'
import { parseUploadOrigin, UPLOAD_ORIGIN_LABELS } from '@/components/screens/upload/upload-origin'
import { UploadWorkspace } from '@/components/screens/upload/upload-workspace'
import { runMutation as runMutationWithFeedback } from '@/lib/run-mutation'
import { userFacingErrorMessage } from '@/lib/user-facing-error-message'

export const Route = createFileRoute('/t/$tenant_id/upload')({
  validateSearch: (search: Record<string, unknown>): { from: UploadOrigin } => ({
    from: parseUploadOrigin(search.from),
  }),
  component: UploadPage,
})

function UploadPage() {
  const { tenant_id: tenantId } = Route.useParams()
  return (
    <div className="flex min-h-screen">
      <AppSidebarContainer tenantId={tenantId} />
      <main className="min-w-0 flex-1">
        <UploadContainer />
      </main>
    </div>
  )
}

type UploadSingleFileResult =
  | { ok: true; fileName: string; s3ObjectId: string; millsheetAutoLink: MillsheetAutoLink | null }
  | { ok: false; message: string }

const supportingMimeTypeSet = new Set<string>(SupportingMimeTypes)

// PDF1件のアップロード本体（createS3Object → 署名付きURLへPUT → サムネイル生成）。
// v1 apps/frontend/client/src/components/FilesUploader.tsx の customRequest 部分の移植。
// GraphQL呼び出しはrouteのみに置く方針のため、この関数もroute内に置く。
async function uploadSingleFile(
  client: ApiClient,
  resourceType: UploadFileType,
  file: File,
): Promise<UploadSingleFileResult> {
  if (!supportingMimeTypeSet.has(file.type)) {
    return { ok: false, message: 'サポートされていないファイル形式です' }
  }
  const contentType = file.type
  const contentLength = file.size

  try {
    const createResult = await client.createS3Object({
      input: { resourceType, contentType, contentLength, originalFileName: file.name },
    })
    const s3Object = createResult.data?.createS3Object
    if (!isSome(s3Object)) {
      return { ok: false, message: 'アップロードに失敗しました' }
    }

    const putResponse = await fetch(s3Object.fileUrlForPut, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType, 'Content-Length': contentLength.toString() },
    })
    if (!putResponse.ok) {
      return { ok: false, message: 'アップロードに失敗しました' }
    }

    // サムネイル生成に失敗しても、アップロード自体（S3Object作成・ファイル本体のPUT）は成功しているため
    // 全体を失敗扱いにはしない（v1 FilesUploader と同じ判断）
    try {
      await client.createS3ObjectThumbnails({ s3ObjectId: s3Object.id })
    } catch {
      // 何もしない（サムネイルが無いだけで一覧・紐付けには影響しない）
    }

    return { ok: true, fileName: file.name, s3ObjectId: s3Object.id, millsheetAutoLink: s3Object.millsheetAutoLink }
  } catch (error) {
    return { ok: false, message: userFacingErrorMessage(error, 'アップロードに失敗しました') }
  }
}

async function openS3ObjectFile(client: ApiClient, s3ObjectId: string) {
  try {
    const result = await client.fetchPagedS3Objects({ ids: [s3ObjectId] })
    const found = result.pagedS3Objects.s3Objects[0]
    if (isSome(found)) {
      window.open(found.fileUrl, '_blank', 'noopener,noreferrer')
    }
  } catch {
    // ダウンロード失敗は他の操作をブロックしない（無言で失敗させる）
  }
}

function UploadContainer() {
  const navigate = useNavigate()
  const { from } = Route.useSearch()
  const { tenant_id: tenantId } = Route.useParams()
  const client = api.useClient()

  const [resourceType, setResourceType] = useState<UploadFileType | undefined>(undefined)
  // 種別ごとに「この画面でアップロードしたファイルのID」を保持する。種別を切り替えても、
  // それぞれのアップロード結果(Step3)を見失わないようにする（v1 と同じ狙い）
  const [uploadedIdsByType, setUploadedIdsByType] = useState<Partial<Record<UploadFileType, string[]>>>({})
  // ミルシートPDFの自動紐付け結果（S3Object.id → 結果）。
  // ユーザーが手動で紐付けを変えたら実態と合わなくなるため、その時点でエントリを消す
  const [millsheetAutoLinkResults, setMillsheetAutoLinkResults] = useState<Record<string, MillsheetAutoLink>>({})
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  // 種類を切り替えたとき、前の種類のファイル別状況が結果に混ざらないよう種類ごとに持つ（uploadedIdsByType と同じ形）
  const [fileStatusesByType, setFileStatusesByType] = useState<Partial<Record<UploadFileType, PdfUploadFileStatus[]>>>(
    {},
  )
  const [uploading, setUploading] = useState(false)

  const fileStatuses = isSome(resourceType) ? (fileStatusesByType[resourceType] ?? []) : []
  const uploadedIds = isSome(resourceType) ? (uploadedIdsByType[resourceType] ?? []) : []
  const uploadedIdSet = new Set(uploadedIds)

  // Step3（この画面でアップロードしたファイルの確認）用のクエリ。選択中の種別分だけ発行する。
  // 添付資料はミルシートと同じ表・操作を使うため、同じクエリを共用する（ids で絞るので種別の混在は起きない）
  const isMillsheetLinked = isMillsheetLinkedUploadType(resourceType)
  const millsheetFilesQuery = api.useFetchPagedS3ObjectsQuery({
    variables: { ids: uploadedIds },
    skip: !isMillsheetLinked || uploadedIds.length === 0,
  })
  const millsheetUnlinkedQuery = api.useFetchPagedS3ObjectsQuery({
    variables: { ids: uploadedIds, notConnectedOnly: true },
    skip: !isMillsheetLinked || uploadedIds.length === 0,
  })
  const millsheetsQuery = api.useFetchMillsheetsQuery({ variables: {}, skip: !isMillsheetLinked })
  const shearingQuery = api.useFetchPagedShearingCertificatePdfsQuery({
    variables: { ids: uploadedIds, resourceType: 'shearingCertificates' },
    skip: resourceType !== 'shearingCertificates',
  })
  const nestingQuery = api.useFetchPagedNestingDiagramsQuery({
    variables: { s3ObjectIds: uploadedIds },
    skip: resourceType !== 'nestingDiagrams' || uploadedIds.length === 0,
  })

  const refetchCurrent = async () => {
    try {
      if (isMillsheetLinked) {
        await Promise.all([millsheetFilesQuery.refetch(), millsheetUnlinkedQuery.refetch(), millsheetsQuery.refetch()])
      } else if (resourceType === 'shearingCertificates') {
        await shearingQuery.refetch()
      } else if (resourceType === 'nestingDiagrams') {
        await nestingQuery.refetch()
      }
    } catch {
      // 操作自体は成功しているため、一覧が古いことだけを成功扱いのままにする
    }
  }

  // 紐付け・削除等（アップロード自体は対象外）の mutation 実行 + トースト + ResultBanner 用 state 更新を
  // 共通ヘルパーに委譲する（docs/develop/client_ui/screens/states.md「操作の結果は「成功はトースト、失敗はトーストと画面の両方」」）
  const runMutation = async (
    mutate: () => Promise<unknown>,
    successMessage: string,
    failureMessage: string,
  ): Promise<boolean> =>
    await runMutationWithFeedback({
      mutate,
      success: { message: successMessage },
      failure: { message: failureMessage },
      refetch: refetchCurrent,
      onResult: setFeedback,
    })

  const clearAutoLink = (s3ObjectId: string) => {
    setMillsheetAutoLinkResults((prev) => omit(prev, [s3ObjectId]))
  }

  // 戻り値は「全件成功したか」。ファイル別の成否（失敗の理由）は fileStatuses を結果ステップとファイル選択ステップに出す
  const handleUploadFiles = async (files: File[]): Promise<boolean> => {
    if (!isSome(resourceType)) {
      return false
    }
    const currentResourceType = resourceType
    setUploading(true)
    const setFileStatuses = (update: (prev: PdfUploadFileStatus[]) => PdfUploadFileStatus[]) => {
      setFileStatusesByType((all) => ({ ...all, [currentResourceType]: update(all[currentResourceType] ?? []) }))
    }
    setFileStatuses(() =>
      files.map((file, index) => ({ id: `${index}:${file.name}`, name: file.name, status: 'uploading' as const })),
    )

    const succeededIds: string[] = []
    const autoLinkWarnings: string[] = []
    for (const [index, file] of files.entries()) {
      const fileStatusId = `${index}:${file.name}`
      const result = await uploadSingleFile(client, currentResourceType, file)
      if (result.ok) {
        succeededIds.push(result.s3ObjectId)
        if (isSome(result.millsheetAutoLink)) {
          const autoLink = result.millsheetAutoLink
          setMillsheetAutoLinkResults((prev) => ({ ...prev, [result.s3ObjectId]: autoLink }))
          const warning = autoLinkWarning(result.fileName, autoLink)
          if (isSome(warning)) {
            autoLinkWarnings.push(warning)
          }
        }
        setFileStatuses((prev) => prev.map((f) => (f.id === fileStatusId ? { ...f, status: 'success' as const } : f)))
      } else {
        setFileStatuses((prev) =>
          prev.map((f) =>
            f.id === fileStatusId ? { ...f, status: 'error' as const, errorMessage: result.message } : f,
          ),
        )
      }
    }

    if (succeededIds.length > 0) {
      setUploadedIdsByType((prev) => ({
        ...prev,
        [currentResourceType]: [...succeededIds, ...(prev[currentResourceType] ?? [])],
      }))
      const uploadSummary =
        succeededIds.length === files.length
          ? `${succeededIds.length}件アップロードしました`
          : `${succeededIds.length}/${files.length}件アップロードしました（一部失敗）`
      setFeedback({
        type: succeededIds.length === files.length ? (autoLinkWarnings.length > 0 ? 'warning' : 'success') : 'error',
        message: [uploadSummary, ...autoLinkWarnings].join('\n'),
      })
      await refetchCurrent()
    } else {
      setFeedback({ type: 'error', message: 'アップロードに失敗しました' })
    }
    setUploading(false)
    return succeededIds.length === files.length
  }

  const handleLink = async (s3ObjectId: string, millsheetId: string) => {
    const ok = await runMutation(
      async () => await client.updateS3Object({ input: { id: s3ObjectId, millsheetId } }),
      '紐付けました',
      '紐付けに失敗しました',
    )
    if (ok) {
      clearAutoLink(s3ObjectId)
    }
  }

  const handleUnlink = async (s3ObjectId: string) => {
    const ok = await runMutation(
      async () => await client.updateS3Object({ input: { id: s3ObjectId, millsheetId: null } }),
      '紐付けを解除しました',
      '解除に失敗しました',
    )
    if (ok) {
      clearAutoLink(s3ObjectId)
    }
  }

  const handleDeleteS3Object = async (s3ObjectId: string) => {
    const ok = await runMutation(
      async () => await client.deleteS3Object({ id: s3ObjectId }),
      '削除しました',
      '削除に失敗しました',
    )
    if (ok) {
      clearAutoLink(s3ObjectId)
    }
  }

  const handleDetachShearingCertificatePdf = async (linkedShearingCertificateId: string) => {
    await runMutation(
      async () => await client.detachShearingCertificatePdf({ id: linkedShearingCertificateId }),
      'PDFの紐付けを解除しました',
      '解除に失敗しました',
    )
  }

  const handleUpdateIndividualNumber = async (id: string, individualNumber: string) => {
    await runMutation(
      async () => await client.updateNestingDiagram({ id, individualNumber }),
      '更新しました',
      '更新に失敗しました',
    )
  }

  const handleDeleteNestingDiagram = async (id: string) => {
    await runMutation(async () => await client.deleteNestingDiagram({ id }), '削除しました', '削除に失敗しました')
  }

  const millsheetFiles = millsheetFilesQuery.data?.pagedS3Objects.s3Objects ?? []
  const millsheetUnlinkedIds = new Set((millsheetUnlinkedQuery.data?.pagedS3Objects.s3Objects ?? []).map((x) => x.id))
  const millsheetOptions = millsheetsQuery.data?.millsheets ?? []
  const shearingRows = shearingQuery.data?.pagedS3Objects.s3Objects ?? []
  const nestingRows = (nestingQuery.data?.pagedNestingDiagrams.nestingDiagrams ?? []).filter(
    (x) => isSome(x.s3Object) && uploadedIdSet.has(x.s3Object.id),
  )

  return (
    <UploadWorkspace
      resourceType={resourceType}
      onSelectResourceType={setResourceType}
      feedback={feedback}
      onDismissFeedback={() => {
        setFeedback(null)
      }}
      uploading={uploading}
      fileStatuses={fileStatuses}
      onUploadFiles={handleUploadFiles}
      uploadedIds={uploadedIds}
      parent={{
        label: UPLOAD_ORIGIN_LABELS[from],
        onNavigate: () => {
          void navigate(
            from === 'shearing_certificates'
              ? { to: '/t/$tenant_id/shearing_certificates', params: { tenant_id: tenantId } }
              : { to: '/t/$tenant_id', params: { tenant_id: tenantId } },
          )
        },
      }}
      onOpenHistory={(type) => {
        void navigate({
          to: '/t/$tenant_id/upload_history',
          params: { tenant_id: tenantId },
          search: isSome(type) ? { type, from } : { from },
        })
      }}
      millsheet={{
        files: millsheetFiles,
        unlinkedIds: millsheetUnlinkedIds,
        millsheets: millsheetOptions,
        autoLinkResults: millsheetAutoLinkResults,
        loading: millsheetFilesQuery.loading,
        onLink: (s3ObjectId, millsheetId) => {
          void handleLink(s3ObjectId, millsheetId)
        },
        onUnlink: (s3ObjectId) => {
          void handleUnlink(s3ObjectId)
        },
        onDelete: (s3ObjectId) => {
          void handleDeleteS3Object(s3ObjectId)
        },
        onDownload: (s3ObjectId) => {
          void openS3ObjectFile(client, s3ObjectId)
        },
      }}
      shearing={{
        rows: shearingRows,
        loading: shearingQuery.loading,
        onDetach: (linkedShearingCertificateId) => {
          void handleDetachShearingCertificatePdf(linkedShearingCertificateId)
        },
        onDelete: (s3ObjectId) => {
          void handleDeleteS3Object(s3ObjectId)
        },
        onDownload: (s3ObjectId) => {
          void openS3ObjectFile(client, s3ObjectId)
        },
      }}
      nesting={{
        rows: nestingRows,
        loading: nestingQuery.loading,
        onUpdateIndividualNumber: (id, individualNumber) => {
          void handleUpdateIndividualNumber(id, individualNumber)
        },
        onDelete: (id) => {
          void handleDeleteNestingDiagram(id)
        },
        onDownload: (s3ObjectId) => {
          void openS3ObjectFile(client, s3ObjectId)
        },
      }}
    />
  )
}
